import { escapeStringRegexp } from "@prismicio/editor-support/String"
import {
  Badge,
  Box,
  Card,
  CardContent,
  CardFooter,
  Dot,
  ErrorBoundary,
  type FilterOption,
  Icon,
  Image,
  Skeleton,
  Text,
  TextOverflow,
} from "@prismicio/editor-ui"
import { type KeyboardEvent, type MouseEvent, Suspense } from "react"

import { useRepositoryTypes } from "./filterData"

export interface DocumentCardProps {
  documentTypeId: string
  documentUrl: URL
  title: string
  statusColor: "grey" | "green" | "amber" | "purple"
  previewImageUrl?: string
  description?: string
  tags: string[]
  tagsFilter?: Set<FilterOption>
  highlighted?: boolean
  highlightedUID?: string
  searchTerm?: string
  onSelect: () => void
}

export function DocumentCard(props: DocumentCardProps) {
  const {
    documentTypeId,
    documentUrl,
    title,
    statusColor,
    description,
    tags,
    tagsFilter,
    previewImageUrl,
    highlighted,
    highlightedUID,
    searchTerm,
    onSelect,
  } = props

  function onCardSelect(event: MouseEvent | KeyboardEvent) {
    const isClickWithModKey =
      event.nativeEvent instanceof MouseEvent &&
      (event.metaKey || event.ctrlKey)

    if (isClickWithModKey) return window.open(documentUrl, "_blank")

    return onSelect()
  }

  return (
    <Card
      paddingBlock={16}
      onSelect={onCardSelect}
      variant={highlighted ? "highlighted" : undefined}
      color={highlighted ? "purple2" : undefined}
      sx={{ marginBlock: highlighted ? 4 : 0, marginInline: 4, flexShrink: 0 }}
    >
      <CardContent>
        <Box justifyContent="space-between" gap={6}>
          <Box
            flexDirection="column"
            gap={4}
            alignItems="flex-start"
            flexGrow={1}
          >
            <DocumentTypeBadge documentTypeId={documentTypeId} />
            <Title
              title={title}
              statusColor={statusColor}
              searchTerm={searchTerm}
            />
            {highlightedUID && searchTerm && (
              <DocumentUID uid={highlightedUID} searchTerm={searchTerm} />
            )}
            <Description description={description} />
          </Box>
          <PreviewImage previewImageUrl={previewImageUrl} />
        </Box>
      </CardContent>

      {tags.length > 0 && <Footer tags={tags} tagsFilter={tagsFilter} />}
    </Card>
  )
}

interface DocumentTypeBadgeProps {
  documentTypeId: string
}

function DocumentTypeBadge(props: DocumentTypeBadgeProps) {
  const { documentTypeId } = props

  return (
    <ErrorBoundary
      renderError={() => (
        <Badge
          maxWidth={150}
          size="medium"
          title="Type: error"
          icon="description"
        />
      )}
    >
      <Suspense fallback={<Skeleton width={150} height={24} />}>
        <TypeBadge documentTypeId={documentTypeId} />
      </Suspense>
    </ErrorBoundary>
  )
}

function TypeBadge(props: DocumentTypeBadgeProps) {
  const { documentTypeId } = props

  const types = useRepositoryTypes()
  const type = [...types].find((type) => type.id === documentTypeId)

  return (
    <Badge
      size="medium"
      title={`Type: ${type?.label ?? "Unknown"}`}
      icon="description"
    />
  )
}

interface TitleProps {
  title: string
  statusColor: DocumentCardProps["statusColor"]
  searchTerm?: string
}

function Title(props: TitleProps) {
  const { title, statusColor, searchTerm } = props

  return (
    <Box gap={8} alignItems="center">
      <Dot color={statusColor} />
      <HighlightedText text={title} highlight={searchTerm} />
    </Box>
  )
}

interface DescriptionProps {
  description?: string
}

function Description(props: DescriptionProps) {
  const { description } = props

  const shortenedDescription = description?.substring(0, 150)

  if (!shortenedDescription) {
    return (
      <Text variant="smallItalic" color="grey11">
        No description available
      </Text>
    )
  }

  return (
    <Text variant="small" color="grey11">
      {`${shortenedDescription}${
        shortenedDescription !== description ? "..." : ""
      }`}
    </Text>
  )
}

interface PreviewImageProps {
  previewImageUrl?: string
}

function PreviewImage(props: PreviewImageProps) {
  const { previewImageUrl } = props

  return (
    <Box
      width={142}
      height={90}
      backgroundColor="grey2"
      border
      borderRadius={6}
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={4}
      flexShrink={0}
      padding={8}
    >
      {previewImageUrl ? (
        <Image src={previewImageUrl} sizing="cover" />
      ) : (
        <>
          <Icon name="hideImage" size="large" color="grey11" />
          <Text color="grey11" variant="small">
            No preview available
          </Text>
        </>
      )}
    </Box>
  )
}

interface FooterProps {
  tags: string[]
  tagsFilter?: Set<FilterOption>
}

function Footer(props: FooterProps) {
  const { tags } = props

  return (
    <CardFooter>
      <Box gap={4} alignItems="center">
        <Icon name="label" size="small" color="grey11" />
        <Text variant="small" color="grey11">
          Tags:
        </Text>
      </Box>

      <TextOverflow>
        {tags.map((tag) => (
          <Badge size="medium" key={tag} title={tag} sx={{ marginRight: 8 }} />
        ))}
      </TextOverflow>
    </CardFooter>
  )
}

interface DocumentUIDProps {
  uid: string
  searchTerm: string
}

function DocumentUID(props: DocumentUIDProps) {
  const { uid, searchTerm } = props

  return (
    <Box alignItems="flex-start">
      <Icon name="tag" color="green11" sx={{ width: 12, height: 12 }} />
      <HighlightedText variant="uid" highlight={searchTerm} text={uid} />
    </Box>
  )
}

interface HighlightedTextProps {
  text: string
  highlight?: string
  variant?: "title" | "uid"
}

// TODO: [PBD-1961] Remove this manual highlighting with OpenSearch highlighting
// https://linear.app/prismic/issue/PBD-1961/use-opensearch-highlight-matching-instead-of-manually-splitting
function HighlightedText(props: HighlightedTextProps) {
  const { text, highlight, variant = "title" } = props
  const config = highlightedTextVariantConfig[variant]

  const parts = highlight
    ? text.split(new RegExp(`(${escapeStringRegexp(highlight)})`, "gi"))
    : [text]

  return (
    <Text
      variant={config.textVariants.text}
      color={config.colors.text}
      hyphens
      overflowWrap="anywhere"
    >
      {parts.map((part, index) =>
        part.toLowerCase() === highlight?.toLowerCase() ? (
          <Text
            variant={config.textVariants.highlight}
            key={index}
            hyphens
            overflowWrap="anywhere"
            highlight
            highlightColor={config.colors.highlight}
            highlightBorderRadius={2}
            color={config.colors.highlightText}
          >
            {part}
          </Text>
        ) : (
          part
        ),
      )}
    </Text>
  )
}

const highlightedTextVariantConfig = {
  title: {
    colors: {
      text: undefined,
      highlight: undefined,
      highlightText: "purple11",
    },
    textVariants: {
      text: "bold",
      highlight: "bold",
    },
  },
  uid: {
    colors: {
      text: "green11",
      highlight: "green3",
      highlightText: "green11",
    },
    textVariants: {
      text: "extraSmall",
      highlight: "extraSmall",
    },
  },
} as const
